import _ from 'lodash'

import React from 'react'

import { Child } from 'services/child/models'
import { BasicViewProps } from 'constants/models'
import Pagination, { PaginationProps } from 'components/Pagination'
import { NEUTRAL_COLOURS } from 'constants/colors'
import { DailyDiaryMedication } from 'services/dailyDiaryMedications/models'
import { RegisterChild } from 'services/legacy/register/models'

import { generateRoute } from 'utils/routing'

import { Avatar, Button, Callout, EmptyState, InfiniteScroll, List, Spinner, Toolbar, Typography } from 'components'

import i18n from 'translations'

import { StyledPageDescription } from '../Activities/ActivitiesStyled'
import MedicationList from './components/MedicationList/MedicationList'
import { AdministerParams } from './MedicationsContainer'
import { StyledMedicationAvatar } from './MedicationsStyled'

interface MedicationsViewProps extends BasicViewProps, PaginationProps {
  dailyDiaryMedicationList?: DailyDiaryMedication[]
  isCurrentDate?: boolean
  onAdministerClick: (params?: AdministerParams) => void
  onEmergencyDoseClick: (child?: Child) => void
  onParentSignatureClick: (id: number) => void
  registers: RegisterChild[]
}

const MedicationsView: React.FC<MedicationsViewProps> = ({
  dailyDiaryMedicationList,
  errorMessages,
  isCurrentDate,
  isLoading,
  onAdministerClick,
  onEmergencyDoseClick,
  onPageChange,
  onParentSignatureClick,
  page,
  pageCount,
  registers,
}) => {
  const renderHeader = () => (
    <Toolbar>
      <Toolbar.Group>
        <Toolbar.Item>
          <StyledPageDescription>
            <Typography>
              {i18n.t('module:DailyDiary:Medication:listInfo')}
            </Typography>
          </StyledPageDescription>
        </Toolbar.Item>
      </Toolbar.Group>
      {registers?.length && !isLoading ? (
        <Toolbar.Group>
          {registers?.length && dailyDiaryMedicationList?.length && isCurrentDate ? (
            <Toolbar.Item>
              <Button
                hierarchy="secondary"
                icon="plus"
                label={i18n.t('module:Safeguarding:Administration:Add:scheduledDoseButtonLabel')}
                negativeMargins
                onClick={() => onAdministerClick()}
              />
            </Toolbar.Item>
          ) : null}
          <Toolbar.Item>
            <Button
              icon="emergency-dose"
              label={i18n.t('module:Safeguarding:Administration:Add:emergencyDoseButtonLabel')}
              negativeMargins
              onClick={() => onEmergencyDoseClick()}
            />
          </Toolbar.Item>
        </Toolbar.Group>
      ) : null}
    </Toolbar>
  )

  const renderPagination = () => (
    <Pagination
      totalResults={dailyDiaryMedicationList?.length}
    />
  )

  const renderItem = (item: DailyDiaryMedication) => {
    const { childRegister: { child }, diaryRecords } = item
    const { firstName, id, photo, surname } = child

    return (
      <List.Standard.Row
        content={(
          <MedicationList
            child={child}
            isCurrentDate={isCurrentDate}
            record={diaryRecords}
            onAdministerClick={onAdministerClick}
            onParentSignatureClick={onParentSignatureClick}
          />
        )}
        key={id}
        leftColumn={(
          <StyledMedicationAvatar>
            <Avatar
              borderColor={NEUTRAL_COLOURS.SILVER}
              borderWidth={4}
              direction="vertical"
              initials={[firstName, surname]}
              src={photo}
              title={`${firstName} ${surname}`}
              to={generateRoute('CHILDREN.CHILD', { childId: id })}
            />
          </StyledMedicationAvatar>
        )}
      />
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return <Spinner />
    }

    if (!registers?.length) {
      return (
        <EmptyState
          icon="daily-diary"
          text1={i18n.t('module:DailyDiary:global:noChildrenAttending')}
        />
      )
    }

    if (!dailyDiaryMedicationList?.length) {
      return (
        <EmptyState
          icon="daily-diary"
          text1={i18n.t('module:DailyDiary:Medication:emptyStateText1')}
          text2={i18n.t('module:DailyDiary:Medication:emptyStateText2')}
        />
      )
    }

    return (
      <React.Fragment>
        {renderPagination()}
        <InfiniteScroll
          dataLength={dailyDiaryMedicationList?.length || 0}
          hasMore={page < pageCount}
          isFetching={isLoading}
          next={() => onPageChange((+page) + 1)}
        >
          <List.Advanced>
            {_.map(dailyDiaryMedicationList, renderItem)}
          </List.Advanced>
        </InfiniteScroll>
        {renderPagination()}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Callout content={errorMessages} error />
      {renderHeader()}
      {renderContent()}
    </React.Fragment>
  )
}

export default MedicationsView
